import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Palette, Images, Frame } from "lucide-react";
import { asset } from "../../lib/api";
import { useArt } from "../../lib/art";
import { ArtShell } from "../../components/art/ArtShell";
import { usePremiumPrompt, CrownBadge } from "../../components/billing/PremiumPrompt";

const Card = ({ a, i, onTap }) => (
  <motion.button data-testid={`art-activity-${a.activity_id}`} onClick={() => onTap(a)} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
    whileHover={{ y: -5 }} whileTap={{ scale: 0.96 }} className="text-left rounded-[1.75rem] bg-white border-4 border-[#f6e2c8] overflow-hidden shadow-sm">
    <div className="relative aspect-square bg-white">
      <img src={asset(a.line_url)} alt="" className={`w-full h-full object-contain p-3 ${a.state === "premium" ? "opacity-40" : ""}`} />
      {a.state === "premium" && <span className="absolute top-2 right-2 w-10 h-10 rounded-full bg-white shadow flex items-center justify-center" data-testid={`art-activity-premium-${a.activity_id}`}><CrownBadge /></span>}
      {a.done && <span className="absolute top-2 left-2 w-9 h-9 rounded-full bg-[#3E9C8F] text-white flex items-center justify-center" data-testid={`art-activity-done-${a.activity_id}`}><Check /></span>}
    </div>
    <div className="px-3 py-2 text-lg font-bold text-stone-700 truncate">{a.title}</div>
  </motion.button>
);

const BigLink = ({ testid, onClick, icon, title, sub, tone }) => (
  <motion.button data-testid={testid} onClick={onClick} whileHover={{ y: -4 }} whileTap={{ scale: 0.97 }}
    className={`rounded-[2rem] p-5 text-left text-white shadow-lg flex items-center gap-4 ${tone}`}>
    <span className="w-16 h-16 rounded-2xl bg-white/25 flex items-center justify-center shrink-0">{icon}</span>
    <span><span className="block text-2xl font-black">{title}</span><span className="block text-white/85">{sub}</span></span>
  </motion.button>
);

const ART_COPY = { title: "這是完整版的畫坊", desc: "請爸爸媽媽打開 Premium，就能畫所有的美術活動，還能把作品掛上小畫廊喔！", later: "先畫免費的" };

export default function ArtHall() {
  const nav = useNavigate();
  const [data] = useArt();
  const [prompt, premiumEl] = usePremiumPrompt(ART_COPY);
  const tap = (a) => (a.state === "premium" ? prompt() : nav(`/art/a/${a.activity_id}`));
  const kinds = data ? Object.entries(data.kinds).sort((x, y) => x[1].order - y[1].order) : [];
  return (
    <ArtShell title="小書童畫坊" back="/halls" testid="art-hall-page">
      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        <BigLink testid="art-free-board-btn" onClick={() => nav("/art/free")} icon={<Palette size={34} />} title="自由畫板" sub="想畫什麼就畫什麼" tone="bg-[#E2683C]" />
        <BigLink testid="art-works-btn" onClick={() => nav("/art/works")} icon={<Images size={34} />} title="我的作品集" sub={data ? `已保存 ${data.works_count}${data.works_limit ? ` / ${data.works_limit}` : ""} 幅` : "…"} tone="bg-[#3E9C8F]" />
        <BigLink testid="art-gallery-btn" onClick={() => (data?.full ? nav("/art/gallery") : prompt())} icon={<Frame size={34} />} title={<>我的小畫廊{!data?.full && " 👑"}</>} sub="把作品掛上牆展示" tone="bg-[#1D6FB8]" />
      </div>
      {kinds.map(([k, meta]) => (
        <section key={k} className="mb-10" data-testid={`art-section-${k}`}>
          <h2 className="text-2xl sm:text-3xl font-black text-stone-800 mb-4"><span className="mr-2">{meta.emoji}</span>{meta.name}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {data.activities.filter((a) => a.kind === k).map((a, i) => <Card key={a.activity_id} a={a} i={i} onTap={tap} />)}
          </div>
        </section>
      ))}
      {premiumEl}
    </ArtShell>
  );
}
